import React, { useState, useMemo, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { useAniList } from '../context/AniListContext';
import { useBatchOperation } from '../context/BatchOperationContext';
import { getRelationTypeDisplay, formatReleaseDate } from '../services/groupingService';
import { getDisplayTitle } from '../hooks/useDisplayTitle';
import { useTitlePreference } from '../context/TitlePreferenceContext';
import { groupPendingAnimeByRelation, sortPendingGroups } from '../services/discoverGroupingService';
import { getRemovedAnimeIds, addRemovedAnime, initRemovedAnimeIds } from '../services/removedAnimeService';
import { fetchRelationsForIds } from '../services/parallelFetcher';
import { useAlert } from '../context/ToastContext';
import { EXCLUDED_DISPLAY_RELATION_TYPES } from '../config/constants';
import RelationTree from './RelationTree';
import ConfirmationModal from './ConfirmationModal';
import './NewRelatedAnime.css';
import './NewRelatedAnimeGrouped.css';
import './Search.css'; // Reuse modal styles from Search

const NewRelatedAnime = ({ onRefresh, showGrouped, setShowGrouped }) => {
    const { pendingAnime, animeGroups, addPendingAnimeToList, token, isProcessingGroups, refreshData, userData, clearGroupsCache, relationsData } = useAniList();
    const { titlePreference } = useTitlePreference();
    const batchOp = useBatchOperation();
    const { showError, showWarning, showInfo } = useAlert();
    const [selectedAnime, setSelectedAnime] = useState(new Set());
    const [isAdding, setIsAdding] = useState(false);
    const [addStatus, setAddStatus] = useState('PLANNING');
    const [filterRelationType, setFilterRelationType] = useState('ALL');
    const [successMessage, setSuccessMessage] = useState('');
    const [isFindingRelations, setIsFindingRelations] = useState(false); // Local loading state for Find New Relations
    const [showFindRelationsModal, setShowFindRelationsModal] = useState(false);
    const [detailAnime, setDetailAnime] = useState(null); // For detail overlay modal

    // Grouped view state
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [selectedGroups, setSelectedGroups] = useState(new Set());

    // Tree view state — Fix #5: default all collapsed
    const [collapsedTreeGroups, setCollapsedTreeGroups] = useState(new Set(['__all_collapsed__'])); // sentinel = all collapsed
    const [treeAllExpanded, setTreeAllExpanded] = useState(false);

    // Counter to trigger re-calculation of removedIds without full page refresh
    const [removedAnimeCounter, setRemovedAnimeCounter] = useState(0);

    // Initialize removed anime IDs from database on mount (triggers migration)
    useEffect(() => {
        initRemovedAnimeIds().then(() => {
            setRemovedAnimeCounter(c => c + 1); // Trigger re-render with cached IDs
        });
    }, []);

    // Prevent body scroll when detail modal is open
    useEffect(() => {
        if (detailAnime) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [detailAnime]);

    // Helper to format anime info for modal
    const formatAnimeInfo = (anime) => {
        const STATUS_MAP = { 'FINISHED': 'Finished', 'RELEASING': 'Airing', 'NOT_YET_RELEASED': 'Upcoming', 'CANCELLED': 'Cancelled', 'HIATUS': 'Hiatus' };
        const parts = [];
        if (anime.format) parts.push(anime.format);
        if (anime.episodes) parts.push(`${anime.episodes} eps`);
        if (anime.status) parts.push(STATUS_MAP[anime.status] || anime.status);
        return parts.join(' • ');
    };

    // Get removed anime IDs - recalculate when counter changes
    const removedIds = useMemo(() => getRemovedAnimeIds(), [removedAnimeCounter]);
    // Excluded relation types — uses shared constant from constants.js (single source of truth)
    const UI_EXCLUDED_RELATION_TYPES = EXCLUDED_DISPLAY_RELATION_TYPES;

    // Safety net: filter out excluded relation types at UI level
    const cleanPending = useMemo(() =>
        pendingAnime.filter(anime => !UI_EXCLUDED_RELATION_TYPES.has(anime.relationType))
    , [pendingAnime]);

    // Filter and search pending anime (flat view)
    const filteredPending = useMemo(() => {
        // Exclude removed anime
        let filtered = cleanPending.filter(anime => !removedIds.has(anime.id));

        // Filter by relation type
        if (filterRelationType !== 'ALL') {
            filtered = filtered.filter(anime => anime.relationType === filterRelationType);
        }

        return filtered;
    }, [cleanPending, filterRelationType, removedIds]);

    // Grouped view data
    const pendingGroups = useMemo(() => {
        if (!showGrouped) return [];

        // Filter out removed anime first
        const nonRemovedAnime = cleanPending.filter(anime => !removedIds.has(anime.id));
        let groups = groupPendingAnimeByRelation(nonRemovedAnime, animeGroups);

        // Apply filters to each group's anime
        if (filterRelationType !== 'ALL') {
            groups = groups.map(group => {
                let filtered = [...group.pendingAnime];

                // Filter by relation type
                if (filterRelationType !== 'ALL') {
                    filtered = filtered.filter(anime => anime.relationType === filterRelationType);
                }



                return {
                    ...group,
                    pendingAnime: filtered,
                    stats: { ...group.stats, count: filtered.length }
                };
            }).filter(group => group.pendingAnime.length > 0);
        }

        // Sort groups by name (default)
        return sortPendingGroups(groups, 'name', 'asc');
    }, [cleanPending, animeGroups, showGrouped, filterRelationType, removedIds]);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [loadedPages, setLoadedPages] = useState(new Set([1]));
    const ITEMS_PER_PAGE = 15;

    // Calculate total pages based on current view
    const totalPages = Math.ceil((showGrouped ? pendingGroups.length : filteredPending.length) / ITEMS_PER_PAGE);

    // Calculate displayed items (groups or anime)
    const displayedItems = useMemo(() => {
        const itemsToShow = [];
        const sourceData = showGrouped ? pendingGroups : filteredPending;

        // Add items from all loaded pages
        Array.from(loadedPages).sort((a, b) => a - b).forEach(page => {
            const startIndex = (page - 1) * ITEMS_PER_PAGE;
            const endIndex = startIndex + ITEMS_PER_PAGE;
            itemsToShow.push(...sourceData.slice(startIndex, endIndex));
        });

        return itemsToShow;
    }, [pendingGroups, filteredPending, showGrouped, loadedPages]);

    // Reset to page 1 when filters or view mode change
    useEffect(() => {
        setCurrentPage(1);
        setLoadedPages(new Set([1]));
    }, [filterRelationType, showGrouped, cleanPending.length]);

    // Load next page
    const loadNextPage = React.useCallback(() => {
        const nextPage = currentPage + 1;
        if (nextPage <= totalPages && !loadedPages.has(nextPage)) {
            setLoadedPages(prev => new Set([...prev, nextPage]));
            setCurrentPage(nextPage);
        }
    }, [currentPage, totalPages, loadedPages]);

    // Infinite scroll handler
    const handleScroll = React.useCallback(() => {
        const scrollPosition = window.innerHeight + window.scrollY;
        const pageHeight = document.documentElement.scrollHeight;
        const triggerHeight = pageHeight - 500; // Load when 500px from bottom

        if (scrollPosition >= triggerHeight && currentPage < totalPages && !loadedPages.has(currentPage + 1)) {
            loadNextPage();
        }
    }, [currentPage, totalPages, loadedPages, loadNextPage]);

    // Attach scroll listener
    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    // Get unique relation types for filter
    const relationTypes = useMemo(() => {
        const types = new Set(cleanPending.map(anime => anime.relationType));
        return ['ALL', ...Array.from(types)];
    }, [cleanPending]);

    const toggleSelectAnime = (animeId) => {
        const newSelected = new Set(selectedAnime);
        if (newSelected.has(animeId)) {
            newSelected.delete(animeId);
        } else {
            newSelected.add(animeId);
        }
        setSelectedAnime(newSelected);
    };

    const selectAll = () => {
        // Select ALL pending anime (excluding only removed ones)
        const allIds = new Set(cleanPending.filter(anime => !removedIds.has(anime.id)).map(anime => anime.id));
        setSelectedAnime(allIds);
    };

    const deselectAll = () => {
        setSelectedAnime(new Set());
    };

    const toggleGroup = (groupId) => {
        const newExpanded = new Set(expandedGroups);
        if (newExpanded.has(groupId)) {
            newExpanded.delete(groupId);
        } else {
            newExpanded.add(groupId);
        }
        setExpandedGroups(newExpanded);
    };

    const selectAllInGroup = (group) => {
        const newSelected = new Set(selectedAnime);
        group.pendingAnime.forEach(anime => newSelected.add(anime.id));
        setSelectedAnime(newSelected);
    };

    const deselectAllInGroup = (group) => {
        const newSelected = new Set(selectedAnime);
        group.pendingAnime.forEach(anime => newSelected.delete(anime.id));
        setSelectedAnime(newSelected);
    };

    const toggleSelectGroup = (groupId, group) => {
        const newSelected = new Set(selectedAnime);
        const groupAnimeIds = group.pendingAnime.map(a => a.id);

        // Check if all anime in group are already selected
        const allSelected = groupAnimeIds.every(id => selectedAnime.has(id));

        if (allSelected) {
            // Deselect all anime in this group
            groupAnimeIds.forEach(id => newSelected.delete(id));
            selectedGroups.delete(groupId);
        } else {
            // Select all anime in this group
            groupAnimeIds.forEach(id => newSelected.add(id));
            selectedGroups.add(groupId);
        }

        setSelectedAnime(newSelected);
        setSelectedGroups(new Set(selectedGroups));
    };

    const addAllInGroup = async (group) => {
        if (!token) {
            showError('OAuth token required to add anime. Please reconnect with OAuth in Settings.', 'Authentication Required');
            return;
        }

        if (group.pendingAnime.length === 0) return;

        setIsAdding(true);
        const totalCount = group.pendingAnime.length;
        let successCount = 0;
        let failCount = 0;

        // Start batch operation tracking
        batchOp.startOperation('adding', totalCount);

        for (const anime of group.pendingAnime) {
            // Check if cancelled
            if (batchOp.checkCancelled()) {
                console.log('🛑 Batch operation cancelled by user');
                break;
            }

            // Update current item
            batchOp.updateCurrentItem({ id: anime.id, title: anime.title?.romaji || anime.title?.english, attempt: 1 });

            try {
                await addPendingAnimeToList(anime.id, addStatus, true); // skipRefresh=true for batch
                successCount++;
                batchOp.markItemCompleted();

                // Rate limit: 70 req/min = ~857ms between calls
                await new Promise(r => setTimeout(r, 857));
            } catch (error) {
                console.error(`Failed to add anime ${anime.id}:`, error);
                failCount++;
                batchOp.markItemFailed();
            }
        }

        setIsAdding(false);

        // Show completion message
        const wasCancelled = batchOp.isCancelled;
        if (wasCancelled) {
            setSuccessMessage(`⚠️ Cancelled: Added ${successCount}/${totalCount} anime from ${group.name}.`);
        } else if (failCount === 0) {
            setSuccessMessage(`✅ Successfully added all ${totalCount} anime from ${group.name}!`);
        } else {
            setSuccessMessage(`⚠️ Added ${successCount}/${totalCount} anime from ${group.name}. ${failCount} failed.`);
        }

        // End batch operation
        batchOp.endOperation();

        setTimeout(() => setSuccessMessage(''), 5000);

        // Single refresh after entire batch completes
        if (successCount > 0 && refreshData) {
            console.log(`🔄 Batch complete — triggering single refresh for ${successCount} added anime`);
            refreshData(true);
        }
    };

    const removeAllInGroup = async (group) => {
        if (group.pendingAnime.length === 0) return;

        let successCount = 0;
        // Await all database operations to complete
        for (const anime of group.pendingAnime) {
            const success = await addRemovedAnime(anime.id);
            if (success) successCount++;
        }

        if (successCount > 0) {
            setSuccessMessage(`✅ Removed ${successCount} anime from ${group.name}!`);
            setTimeout(() => setSuccessMessage(''), 3000);
            // Use flushSync to ensure immediate synchronous state update
            flushSync(() => {
                setRemovedAnimeCounter(prev => prev + 1);
            });
        }
    };

    const addSingleAnime = async (animeId, status = addStatus) => {
        if (!token) {
            showError('OAuth token required to add anime. Please reconnect with OAuth in Settings.', 'Authentication Required');
            return;
        }

        setIsAdding(true);
        try {
            await addPendingAnimeToList(animeId, status);

            // Remove from selected
            const newSelected = new Set(selectedAnime);
            newSelected.delete(animeId);
            setSelectedAnime(newSelected);

            // Show success message
            setSuccessMessage('Anime added successfully! ✅ Refreshing groups...');

            // Trigger auto-refresh to update groups after a short delay
            if (refreshData) {
                setTimeout(async () => {
                    try {
                        await refreshData(true); // Force refresh with true parameter
                        if (onRefresh) onRefresh(); // Refresh UI
                        setSuccessMessage('Groups updated! ✅');
                    } catch (error) {
                        console.error('Auto-refresh failed:', error);
                    }
                }, 1000);
            }

            setTimeout(() => setSuccessMessage(''), 4000);
        } catch (error) {
            showError(`Failed to add anime: ${error.message}`, 'Add Failed');
        } finally {
            setIsAdding(false);
        }
    };

    const removeSingleAnime = async (animeId) => {
        const success = await addRemovedAnime(animeId);
        if (success) {
            // Remove from selected
            const newSelected = new Set(selectedAnime);
            newSelected.delete(animeId);
            setSelectedAnime(newSelected);

            // Increment counter to trigger removedIds recalculation
            // This preserves expandedGroups state
            // Use flushSync to ensure immediate synchronous state update
            flushSync(() => {
                setRemovedAnimeCounter(prev => prev + 1);
            });

            setSuccessMessage('Anime removed! ✅');
            setTimeout(() => setSuccessMessage(''), 3000);

            // Don't call onRefresh() - it causes full re-render and collapses groups
            // The counter increment above is enough to update the filtered list
        } else {
            showError('Failed to remove anime', 'Remove Failed');
        }
    };

    const addSelectedAnime = async () => {
        if (selectedAnime.size === 0) {
            showWarning('No anime selected', 'No Selection');
            return;
        }

        if (!token) {
            showError('OAuth token required to add anime. Please reconnect with OAuth in Settings.', 'Authentication Required');
            return;
        }

        setIsAdding(true);
        const totalCount = selectedAnime.size;
        let successCount = 0;
        let failCount = 0;

        // Start batch operation tracking
        batchOp.startOperation('adding', totalCount);

        for (const animeId of selectedAnime) {
            // Check if cancelled
            if (batchOp.checkCancelled()) {
                console.log('🛑 Batch operation cancelled by user');
                break;
            }

            // Find anime details for display
            const anime = pendingAnime.find(a => a.id === animeId);
            batchOp.updateCurrentItem({
                id: animeId,
                title: anime?.title?.romaji || anime?.title?.english || `ID: ${animeId}`,
                attempt: 1
            });

            try {
                await addPendingAnimeToList(animeId, addStatus, true); // skipRefresh=true for batch
                successCount++;
                batchOp.markItemCompleted();

                // Rate limit: 70 req/min = ~857ms between calls
                await new Promise(r => setTimeout(r, 857));
            } catch (error) {
                console.error(`Failed to add anime ${animeId}:`, error);
                failCount++;
                batchOp.markItemFailed();
            }
        }

        setSelectedAnime(new Set());
        setIsAdding(false);

        // Show summary
        const wasCancelled = batchOp.isCancelled;
        if (wasCancelled) {
            setSuccessMessage(`⚠️ Cancelled: Added ${successCount}/${totalCount} selected anime.`);
        } else if (failCount === 0) {
            setSuccessMessage(`✅ Successfully added all ${totalCount} anime!`);
        } else {
            setSuccessMessage(`⚠️ Added ${successCount}/${totalCount} anime. ${failCount} failed.`);
        }

        // End batch operation
        batchOp.endOperation();

        setTimeout(() => setSuccessMessage(''), 5000);

        // Single refresh after entire batch completes
        if (successCount > 0 && refreshData) {
            console.log(`🔄 Batch complete — triggering single refresh for ${successCount} added anime`);
            refreshData(true);
        }
    };

    const removeSelectedAnime = async () => {
        if (selectedAnime.size === 0) {
            showWarning('No anime selected', 'No Selection');
            return;
        }

        let successCount = 0;
        // Await all database operations to complete
        for (const animeId of selectedAnime) {
            const success = await addRemovedAnime(animeId);
            if (success) successCount++;
        }

        if (successCount > 0) {
            setSuccessMessage(`✅ Removed ${successCount} selected anime!`);
            setTimeout(() => setSuccessMessage(''), 3000);
            setSelectedAnime(new Set());
            // Use flushSync to ensure immediate synchronous state update
            flushSync(() => {
                setRemovedAnimeCounter(prev => prev + 1);
            });
            // Don't call onRefresh() - it causes full re-render
            // The counter increment above is enough to update the filtered list
        }
    };

    const removeAllPending = async () => {
        const animeToRemove = showGrouped
            ? pendingGroups.flatMap(g => g.pendingAnime)
            : filteredPending;

        if (animeToRemove.length === 0) return;

        const confirm = window.confirm(`Remove all ${animeToRemove.length} anime from this view?`);
        if (!confirm) return;

        let successCount = 0;
        // Await all database operations to complete
        for (const anime of animeToRemove) {
            const success = await addRemovedAnime(anime.id);
            if (success) successCount++;
        }

        if (successCount > 0) {
            setSuccessMessage(`✅ Removed ${successCount} anime!`);
            setTimeout(() => setSuccessMessage(''), 3000);
            setSelectedAnime(new Set());
            setSelectedGroups(new Set());
            // Use flushSync to ensure immediate synchronous state update
            flushSync(() => {
                setRemovedAnimeCounter(prev => prev + 1);
            });
            // Don't call onRefresh() - it causes full re-render
            // The counter increment above is enough to update the filtered list
        }
    };

    // Find new relations by triggering force rebuild in background
    const findNewRelations = async () => {
        if (!userData || !userData.lists || userData.lists.length === 0) {
            showInfo('No anime data loaded. Please wait for your AniList to load and try again.', 'Please Wait');
            return;
        }

        // Show confirmation modal
        setShowFindRelationsModal(true);
    };

    const confirmFindNewRelations = async () => {
        setShowFindRelationsModal(false);

        // Show local loading state
        setIsFindingRelations(true);
        setSuccessMessage('🔄 Finding new relations...');

        // Trigger force rebuild (runs in background)
        try {
            clearGroupsCache();

            // Success message will be shown when rebuild completes
            // The loading UI will automatically hide when isProcessingGroups becomes false
        } catch (error) {
            console.error('Failed to trigger force rebuild:', error);
            setSuccessMessage('⚠️ Failed to start rebuild. Please try again.');
            setIsFindingRelations(false);
            setTimeout(() => setSuccessMessage(''), 3000);
        }
    };

    // Show loading UI only in this tab when processing (either from Find New Relations or global processing)
    if (isProcessingGroups || isFindingRelations) {
        // Clear local loading state when global processing completes
        if (!isProcessingGroups && isFindingRelations) {
            setIsFindingRelations(false);
            setSuccessMessage('✅ New relations discovered! Check the list below.');
            setTimeout(() => setSuccessMessage(''), 5000);
        }

        return (
            <div className="discover-container">
                <div className="loading-state">
                    <div className="loader"></div>
                    <p>{isFindingRelations ? 'Finding new relations...' : 'Processing groups...'}</p>
                    <p style={{ fontSize: '0.85em', opacity: 0.7, marginTop: '8px' }}>
                        💡 If progress appears stuck, don't worry - it's still working in the background
                    </p>
                </div>
            </div>
        );
    }

    if (!pendingAnime || pendingAnime.length === 0) {
        return (
            <div className="discover-container">
                <div className="empty-state">
                    <div className="empty-icon">🎉</div>
                    <h3>All Caught Up!</h3>
                    <p>No related anime found that you haven't added yet.</p>
                    <p>Check back after adding more anime to your list.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="discover-container">
            {/* Header */}
            <div className="discover-header">
                <div className="header-content">
                    <h1>🔍 Discover Related Anime</h1>
                    <p>Found {filteredPending.length} anime related to your list</p>
                </div>
                {/* View Toggle */}
                <div className="view-toggle-group">
                    <button
                        onClick={() => setShowGrouped(false)}
                        className={`view-toggle-btn ${!showGrouped && showGrouped !== 'tree' ? 'active' : ''}`}
                        title="Flat View"
                    >
                        📋 Flat
                    </button>
                    <button
                        onClick={() => setShowGrouped(true)}
                        className={`view-toggle-btn ${showGrouped === true ? 'active' : ''}`}
                        title="Grouped View"
                    >
                        📚 Grouped
                    </button>
                    <button
                        onClick={() => setShowGrouped('tree')}
                        className={`view-toggle-btn ${showGrouped === 'tree' ? 'active' : ''}`}
                        title="Tree View"
                    >
                        🌳 Tree
                    </button>
                </div>
            </div>

            {/* Success Message */}
            {successMessage && (
                <div className="success-banner">
                    {successMessage}
                </div>
            )}

            {/* Batch Operation Progress UI */}
            {batchOp.isOperationActive && (
                <div className="batch-operation-progress" style={{
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    backgroundColor: '#2196F3',
                    color: 'white',
                    padding: '15px 20px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                    zIndex: 1000,
                    minWidth: '300px'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <strong>🔄 {batchOp.operationType === 'adding' ? 'Adding' : 'Updating'} Anime</strong>
                        <button
                            onClick={() => batchOp.cancelOperation()}
                            style={{
                                background: '#f44336',
                                border: 'none',
                                color: 'white',
                                padding: '4px 12px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                        Progress: {batchOp.completedItems}/{batchOp.totalItems} ({batchOp.getProgress()}%)
                    </div>
                    <div style={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: 'rgba(255,255,255,0.3)',
                        borderRadius: '4px',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            width: `${batchOp.getProgress()}%`,
                            height: '100%',
                            backgroundColor: '#4CAF50',
                            transition: 'width 0.3s ease'
                        }}></div>
                    </div>
                    {batchOp.currentItem && (
                        <div style={{ marginTop: '8px', fontSize: '13px', opacity: 0.9 }}>
                            Current: {batchOp.currentItem.title}
                            {batchOp.currentItem.attempt > 1 && ` (Retry ${batchOp.currentItem.attempt})`}
                        </div>
                    )}
                </div>
            )}

            {/* Toolbar */}
            <div className="discover-toolbar">
                {/* Relation Type Filter */}
                <div className="filter-group">
                    <label>Filter by Relation:</label>
                    <select
                        value={filterRelationType}
                        onChange={(e) => setFilterRelationType(e.target.value)}
                    >
                        {relationTypes.map(type => (
                            <option key={type} value={type}>
                                {type === 'ALL' ? 'All Types' : getRelationTypeDisplay(type)}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Add Status */}
                <div className="filter-group">
                    <label>Add as:</label>
                    <select
                        value={addStatus}
                        onChange={(e) => setAddStatus(e.target.value)}
                    >
                        <option value="PLANNING">📅 Planning</option>
                        <option value="CURRENT">▶️ Watching</option>
                        <option value="PAUSED">⏸️ On Hold</option>
                        <option value="COMPLETED">✅ Completed</option>
                        <option value="DROPPED">❌ Dropped</option>
                    </select>
                </div>

                {/* Find New Relations Button */}
                <div className="filter-group">
                    <label>&nbsp;</label>
                    <button
                        onClick={findNewRelations}
                        disabled={isAdding || isFindingRelations || isProcessingGroups}
                        title="Rebuild relations for all anime in your AniList to discover more related anime"
                    >
                        {(isFindingRelations || isProcessingGroups) ? '⏳ Rebuilding...' : '🔄 Find New Relations'}
                    </button>
                </div>
            </div>

            {/* Quick Actions - for grouped view: select all anime */}
            {showGrouped && pendingGroups.length > 0 && (
                <div className="quick-actions">
                    <button onClick={selectedAnime.size > 0 ? deselectAll : selectAll}>
                        {selectedAnime.size > 0 ? '❌ Deselect All' : `✅ Select All (${filteredPending.length})`}
                    </button>
                </div>
            )}

            {/* Quick Actions - only in flat view */}
            {!showGrouped && filteredPending.length > 0 && (
                <div className="quick-actions">
                    <button onClick={selectedAnime.size > 0 ? deselectAll : selectAll}>
                        {selectedAnime.size > 0 ? '❌ Deselect All' : `✅ Select All (${filteredPending.length})`}
                    </button>
                </div>
            )}

            {/* Selection Actions */}
            {selectedAnime.size > 0 && (
                <div className="selection-bar">
                    <span>{selectedAnime.size} selected</span>
                    <div className="selection-actions">
                        <button
                            onClick={addSelectedAnime}
                            disabled={isAdding}
                            className="add-selected-btn"
                        >
                            {isAdding ? '⏳ Adding...' : `➕ Add Selected (${selectedAnime.size})`}
                        </button>
                        <button
                            onClick={removeSelectedAnime}
                            className="remove-selected-btn"
                        >
                            🗑️ Remove Selected
                        </button>
                    </div>
                </div>
            )}

            {/* Tree View */}
            {showGrouped === 'tree' ? (
                <div className="relation-tree-container">
                    {/* Fix #5: Expand/Collapse All Toggle */}
                    {pendingGroups.length > 0 && (
                        <div className="tree-global-controls">
                            <button
                                className="tree-toggle-all-btn"
                                onClick={() => {
                                    if (treeAllExpanded) {
                                        // Collapse all
                                        setCollapsedTreeGroups(new Set(['__all_collapsed__']));
                                        setTreeAllExpanded(false);
                                    } else {
                                        // Expand all
                                        setCollapsedTreeGroups(new Set());
                                        setTreeAllExpanded(true);
                                    }
                                }}
                            >
                                {treeAllExpanded ? '📁 Collapse All' : '📂 Expand All'}
                            </button>
                            <span className="tree-global-count">{pendingGroups.length} groups</span>
                        </div>
                    )}
                    {pendingGroups.length === 0 ? (
                        <div className="empty-state">
                            <p>No anime found matching your filters.</p>
                        </div>
                    ) : (
                        displayedItems.map(group => {
                            // Fix #5: Default collapsed — if sentinel exists, all groups start collapsed
                            // Individual toggles override
                            const isCollapsed = collapsedTreeGroups.has('__all_collapsed__')
                                ? !collapsedTreeGroups.has(`expanded:${group.id}`)
                                : collapsedTreeGroups.has(group.id);

                            return (
                                <RelationTree
                                    key={group.id}
                                    group={group}
                                    relationsData={relationsData}
                                    titlePreference={titlePreference}
                                    selectedAnime={selectedAnime}
                                    onToggleSelect={(id, selected) => {
                                        const newSelected = new Set(selectedAnime);
                                        if (selected) newSelected.add(id);
                                        else newSelected.delete(id);
                                        setSelectedAnime(newSelected);
                                    }}
                                    onAddAnime={addSingleAnime}
                                    onRemoveAnime={removeSingleAnime}
                                    onDetailClick={(node) => setDetailAnime(node)}
                                    isAdding={isAdding}
                                    addStatus={addStatus}
                                    isCollapsed={isCollapsed}
                                    onToggleCollapse={(groupId) => {
                                        setCollapsedTreeGroups(prev => {
                                            const next = new Set(prev);
                                            if (next.has('__all_collapsed__')) {
                                                // In "all collapsed" mode — toggle individual expand
                                                const expandKey = `expanded:${groupId}`;
                                                if (next.has(expandKey)) {
                                                    next.delete(expandKey);
                                                } else {
                                                    next.add(expandKey);
                                                }
                                            } else {
                                                // In "all expanded" mode — toggle individual collapse
                                                if (next.has(groupId)) {
                                                    next.delete(groupId);
                                                } else {
                                                    next.add(groupId);
                                                }
                                            }
                                            return next;
                                        });
                                    }}
                                />
                            );
                        })
                    )}
                </div>
            ) : showGrouped === true ? (

            /* Grouped View */
                <div className="pending-groups-container">
                    {pendingGroups.length === 0 ? (
                        <div className="empty-state">
                            <p>No anime found matching your filters.</p>
                        </div>
                    ) : (
                        pendingGroups.map(group => {
                            const isExpanded = expandedGroups.has(group.id);
                            const groupSelectedCount = group.pendingAnime.filter(anime =>
                                selectedAnime.has(anime.id)
                            ).length;

                            return (
                                <div key={group.id} className={`pending-group-card ${isExpanded ? 'expanded' : ''} ${selectedGroups.has(group.id) ? 'group-selected' : ''}`}>
                                    {/* Group Header */}
                                    <div className="pending-group-header">
                                        {/* Group Selection Checkbox */}
                                        <div className="group-selection-checkbox">
                                            <input
                                                type="checkbox"
                                                checked={group.pendingAnime.every(a => selectedAnime.has(a.id))}
                                                onChange={(e) => {
                                                    e.stopPropagation();
                                                    toggleSelectGroup(group.id, group);
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                        <div className="group-header-main" onClick={() => toggleGroup(group.id)}>
                                            <div className="group-title-section">
                                                <h2 className="group-name">
                                                    {isExpanded ? '📖' : '📚'} {group.name}
                                                </h2>
                                                <div className="group-stats-mini">
                                                    <span className="stat-count">{group.stats.count} anime</span>
                                                    {group.stats.averageScore > 0 && (
                                                        <>
                                                            <span>•</span>
                                                            <span className="stat-score">⭐ {(group.stats.averageScore / 10).toFixed(1)}</span>
                                                        </>
                                                    )}
                                                    {group.stats.totalEpisodes > 0 && (
                                                        <>
                                                            <span>•</span>
                                                            <span className="stat-episodes">🎬 {group.stats.totalEpisodes} eps</span>
                                                        </>
                                                    )}
                                                    {groupSelectedCount > 0 && (
                                                        <>
                                                            <span>•</span>
                                                            <span className="stat-selected">✅ {groupSelectedCount} selected</span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            <button className="expand-btn" title={isExpanded ? 'Collapse' : 'Expand'}>
                                                {isExpanded ? '▼' : '▶'}
                                            </button>
                                        </div>

                                        {/* Group Actions */}
                                        <div className="group-actions">
                                            <button
                                                onClick={() => groupSelectedCount === group.stats.count ? deselectAllInGroup(group) : selectAllInGroup(group)}
                                                className="group-action-btn"
                                                title={groupSelectedCount === group.stats.count ? "Deselect all in this group" : "Select all in this group"}
                                            >
                                                {groupSelectedCount === group.stats.count ? '❌ Deselect All' : '✅ Select All'}
                                            </button>
                                            <button
                                                onClick={() => addAllInGroup(group)}
                                                disabled={isAdding}
                                                className="group-action-btn add-group-btn"
                                                title="Add all anime in this group"
                                            >
                                                {isAdding ? '⏳ Adding...' : `➕ Add All (${group.stats.count})`}
                                            </button>
                                            <button
                                                onClick={() => removeAllInGroup(group)}
                                                className="group-action-btn remove-group-btn"
                                                title="Remove all anime in this group"
                                            >
                                                🗑️ Remove All ({group.stats.count})
                                            </button>
                                        </div>
                                    </div>

                                    {/* Group Content (Expanded) */}
                                    {isExpanded && (
                                        <div className="pending-group-content">
                                            {group.pendingAnime.map(anime => {
                                                const title = getDisplayTitle(anime, titlePreference);
                                                const releaseDate = formatReleaseDate(anime.startDate);
                                                const isSelected = selectedAnime.has(anime.id);

                                                return (
                                                    <div
                                                        key={anime.id}
                                                        className={`pending-card ${isSelected ? 'selected' : ''}`}
                                                        onClick={() => setDetailAnime(anime)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        {/* Selection Checkbox */}
                                                        <div className="selection-checkbox" onClick={(e) => e.stopPropagation()}>
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => toggleSelectAnime(anime.id)}
                                                            />
                                                        </div>

                                                        {/* Cover Image */}
                                                        <div className="anime-cover-container">
                                                            <img
                                                                src={anime.coverImage?.large || anime.coverImage?.medium}
                                                                alt={title}
                                                                className="anime-cover"
                                                            />
                                                        </div>

                                                        {/* Anime Info */}
                                                        <div className="anime-info">
                                                            <h3 className="anime-title">
                                                                <a
                                                                    href={`https://anilist.co/anime/${anime.id}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    {title}
                                                                </a>
                                                            </h3>

                                                            {/* Relation Info */}
                                                            <div className="relation-info">
                                                                <span className="relation-badge">
                                                                    {getRelationTypeDisplay(anime.relationType)}
                                                                </span>
                                                                {anime.relatedTo && (
                                                                    <span className="related-to">
                                                                        → {anime.relatedTo.title}
                                                                    </span>
                                                                )}
                                                            </div>

                                                            {/* Meta */}
                                                            <div className="anime-meta">
                                                                <span>📅 {releaseDate}</span>
                                                                {anime.episodes && (
                                                                    <>
                                                                        <span>•</span>
                                                                        <span>🎬 {anime.episodes} eps</span>
                                                                    </>
                                                                )}
                                                                {anime.averageScore && (
                                                                    <>
                                                                        <span>•</span>
                                                                        <span>⭐ {(anime.averageScore / 10).toFixed(1)}</span>
                                                                    </>
                                                                )}
                                                                {anime.format && (
                                                                    <>
                                                                        <span>•</span>
                                                                        <span>{anime.format}</span>
                                                                    </>
                                                                )}
                                                                {anime.status && (
                                                                    <>
                                                                        <span>•</span>
                                                                        <span>{({ 'FINISHED': 'Finished', 'RELEASING': 'Airing', 'NOT_YET_RELEASED': 'Upcoming', 'CANCELLED': 'Cancelled', 'HIATUS': 'Hiatus' })[anime.status] || anime.status}</span>
                                                                    </>
                                                                )}
                                                            </div>

                                                            {/* Description (truncated) */}
                                                            {anime.description && (
                                                                <p className="anime-description">
                                                                    {anime.description.replace(/<[^>]*>/g, '').substring(0, 150)}...
                                                                </p>
                                                            )}

                                                            {/* Quick Add Button */}
                                                            <div className="anime-actions" onClick={(e) => e.stopPropagation()}>
                                                                <button
                                                                    onClick={() => addSingleAnime(anime.id)}
                                                                    disabled={isAdding}
                                                                    className="add-btn"
                                                                >
                                                                    {isAdding ? '⏳ Adding...' : '➕ Add to List'}
                                                                </button>
                                                                <button
                                                                    onClick={() => removeSingleAnime(anime.id)}
                                                                    className="remove-btn"
                                                                >
                                                                    🗑️ Remove
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            ) : (
                /* Flat View - Original List */
                <div className="pending-list">
                    {displayedItems.length === 0 ? (
                        <div className="empty-state">
                            <p>No anime found matching your filters.</p>
                        </div>
                    ) : (
                        displayedItems.map(anime => {
                            const title = getDisplayTitle(anime, titlePreference);
                            const releaseDate = formatReleaseDate(anime.startDate);
                            const isSelected = selectedAnime.has(anime.id);

                            return (
                                <div
                                    key={anime.id}
                                    className={`pending-card ${isSelected ? 'selected' : ''}`}
                                    onClick={() => setDetailAnime(anime)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {/* Selection Checkbox */}
                                    <div className="selection-checkbox" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => toggleSelectAnime(anime.id)}
                                        />
                                    </div>

                                    {/* Cover Image */}
                                    <div className="anime-cover-container">
                                        <img
                                            src={anime.coverImage?.large || anime.coverImage?.medium}
                                            alt={title}
                                            className="anime-cover"
                                        />
                                    </div>

                                    {/* Anime Info */}
                                    <div className="anime-info">
                                        <h3 className="anime-title">
                                            <a
                                                href={`https://anilist.co/anime/${anime.id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {title}
                                            </a>
                                        </h3>

                                        {/* Relation Info */}
                                        <div className="relation-info">
                                            <span className="relation-badge">
                                                {getRelationTypeDisplay(anime.relationType)}
                                            </span>
                                            {anime.relatedTo && (
                                                <span className="related-to">
                                                    → {anime.relatedTo.title}
                                                </span>
                                            )}
                                        </div>

                                        {/* Meta */}
                                        <div className="anime-meta">
                                            <span>📅 {releaseDate}</span>
                                            {anime.episodes && (
                                                <>
                                                    <span>•</span>
                                                    <span>🎬 {anime.episodes} eps</span>
                                                </>
                                            )}
                                            {anime.averageScore && (
                                                <>
                                                    <span>•</span>
                                                    <span>⭐ {(anime.averageScore / 10).toFixed(1)}</span>
                                                </>
                                            )}
                                            {anime.format && (
                                                <>
                                                    <span>•</span>
                                                    <span>{anime.format}</span>
                                                </>
                                            )}
                                            {anime.status && (
                                                <>
                                                    <span>•</span>
                                                    <span>{({ 'FINISHED': 'Finished', 'RELEASING': 'Airing', 'NOT_YET_RELEASED': 'Upcoming', 'CANCELLED': 'Cancelled', 'HIATUS': 'Hiatus' })[anime.status] || anime.status}</span>
                                                </>
                                            )}
                                        </div>

                                        {/* Quick Add Button - same as grouped view */}
                                        <div className="anime-actions" onClick={(e) => e.stopPropagation()}>
                                            <button
                                                onClick={() => addSingleAnime(anime.id)}
                                                disabled={isAdding}
                                                className="add-btn"
                                            >
                                                {isAdding ? '⏳ Adding...' : '➕ Add to List'}
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); removeSingleAnime(anime.id); }}
                                                className="remove-btn"
                                            >
                                                🗑️ Remove
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

            )}

            {/* Summary Footer */}
            <div className="discover-summary">
                {showGrouped ? (
                    <p>
                        Showing {pendingGroups.length} group{pendingGroups.length !== 1 ? 's' : ''} with{' '}
                        {pendingGroups.reduce((sum, g) => sum + g.stats.count, 0)} of {pendingAnime.length} pending anime
                    </p>
                ) : (
                    <p>
                        Showing {filteredPending.length} of {pendingAnime.length} pending anime
                    </p>
                )}
            </div>

            {/* Find New Relations Confirmation Modal */}
            <ConfirmationModal
                isOpen={showFindRelationsModal}
                onConfirm={confirmFindNewRelations}
                onCancel={() => setShowFindRelationsModal(false)}
                title="Find New Relations?"
                message={`This will rebuild relations for all ${userData?.lists?.flatMap(list => list.entries || []).length || 0} anime in your AniList to discover new relations.\n\nThis may take a few minutes. Continue?`}
                confirmText="Find Relations"
                cancelText="Cancel"
            />

            {/* Anime Detail Overlay Modal (same as Discover) */}
            {detailAnime && (
                <div className="anime-modal-overlay" onClick={() => setDetailAnime(null)}>
                    <div className="anime-modal" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="modal-close-btn"
                            onClick={() => setDetailAnime(null)}
                        >
                            ✕
                        </button>

                        {detailAnime.bannerImage && (
                            <img
                                src={detailAnime.bannerImage}
                                alt=""
                                className="anime-banner"
                            />
                        )}

                        <div className="modal-content">
                            <div className="modal-header">
                                {(detailAnime.coverImage?.large || detailAnime.coverImage?.medium) && (
                                    <img
                                        src={detailAnime.coverImage?.large || detailAnime.coverImage?.medium}
                                        alt={getDisplayTitle(detailAnime, titlePreference)}
                                        className="modal-cover"
                                    />
                                )}
                                <div className="modal-header-info">
                                    <h2>
                                        <a
                                            href={`https://anilist.co/anime/${detailAnime.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {getDisplayTitle(detailAnime, titlePreference)}
                                        </a>
                                    </h2>
                                    {detailAnime.title?.english && detailAnime.title.english !== detailAnime.title?.romaji && (
                                        <p className="alt-title">{detailAnime.title.english}</p>
                                    )}

                                    {/* Fix #2: Beautified meta badges */}
                                    <div className="modal-meta-badges">
                                        {detailAnime.format && (
                                            <span className="modal-badge format-badge">{detailAnime.format}</span>
                                        )}
                                        {detailAnime.episodes && (
                                            <span className="modal-badge episodes-badge">🎬 {detailAnime.episodes} eps</span>
                                        )}
                                        {detailAnime.duration && (
                                            <span className="modal-badge duration-badge">⏱️ {detailAnime.duration} min/ep</span>
                                        )}
                                        {detailAnime.status && (
                                            <span className={`modal-badge status-badge status-${detailAnime.status?.toLowerCase()}`}>
                                                {({ 'FINISHED': '✅ Finished', 'RELEASING': '📡 Airing', 'NOT_YET_RELEASED': '🔜 Upcoming', 'CANCELLED': '🚫 Cancelled', 'HIATUS': '⏸️ Hiatus' })[detailAnime.status] || detailAnime.status}
                                            </span>
                                        )}
                                        {detailAnime.startDate?.year && (
                                            <span className="modal-badge date-badge">📅 {formatReleaseDate(detailAnime.startDate)}</span>
                                        )}
                                    </div>

                                    {/* Fix #2: Beautified score card */}
                                    {detailAnime.averageScore && (
                                        <div className="modal-score-card">
                                            <div className="modal-score-value">
                                                <span className="modal-score-star">★</span>
                                                <span className="modal-score-number">{(detailAnime.averageScore / 10).toFixed(1)}</span>
                                                <span className="modal-score-max">/10</span>
                                            </div>
                                            <div className="modal-score-bar">
                                                <div className="modal-score-fill" style={{ width: `${detailAnime.averageScore}%` }} />
                                            </div>
                                        </div>
                                    )}

                                    {/* User status for green cards */}
                                    {detailAnime.isInUserList && detailAnime.userStatus && (
                                        <div className="modal-user-status">
                                            <span className="modal-badge user-status-badge">
                                                {({ 'CURRENT': '▶️ Watching', 'PLANNING': '📅 Planning', 'COMPLETED': '✅ Completed', 'DROPPED': '❌ Dropped', 'PAUSED': '⏸️ On Hold', 'REPEATING': '🔁 Rewatching' })[detailAnime.userStatus] || detailAnime.userStatus}
                                            </span>
                                        </div>
                                    )}

                                    {detailAnime.relationType && (
                                        <div className="relation-info" style={{ marginTop: '0.5rem' }}>
                                            <span className="relation-badge">
                                                {getRelationTypeDisplay(detailAnime.relationType)}
                                            </span>
                                            {detailAnime.relatedTo && (
                                                <span className="related-to">→ {detailAnime.relatedTo.title}</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {detailAnime.description && (
                                <div className="modal-description">
                                    <h3>Description</h3>
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: detailAnime.description
                                        }}
                                    />
                                </div>
                            )}

                            {detailAnime.genres && detailAnime.genres.length > 0 && (
                                <div className="modal-genres">
                                    <h3>Genres</h3>
                                    <div className="genre-list">
                                        {detailAnime.genres.map((genre, idx) => (
                                            <span key={idx} className="genre-tag-large">
                                                {genre}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="modal-actions">
                                <a
                                    href={`https://anilist.co/anime/${detailAnime.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-anilist"
                                >
                                    View on AniList
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NewRelatedAnime;
